import { useEffect, useState, useCallback, useMemo } from "react";
import {
  getSettings, saveEntry, getEntry, getTasks, getHabitLog, toggleHabit,
  completeTask, reopenTask, postponeTask,
} from "../api";
import { todayISO, computeProgress } from "../lib/planner";
import { CheckInForm } from "../components/CheckInForm";
import { CapacityHero } from "../components/CapacityHero";
import { TaskList } from "../components/TaskList";
import { TodaySchedule } from "../components/TodaySchedule";
import { DailyHabits } from "../components/DailyHabits";
import { SupplementsCard } from "../components/SupplementsCard";
import { FocusBlockDialog } from "../components/FocusBlockDialog";
import { WinsRecap } from "../components/WinsRecap";
import { Celebration } from "../components/Celebration";
import { Button } from "../components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const DONE_MESSAGES = ["One kind win 🌿", "Done — steady you", "That's progress", "Beautifully done", "Look at you go 🌸"];
const markCompleted = (data, id, done) => {
  const fix = (arr) => {
    const next = arr.map((t) => (t.id === id ? { ...t, completed: done } : t));
    return [...next.filter((t) => !t.completed), ...next.filter((t) => t.completed)];
  };
  return { ...data, top3: fix(data.top3), fits_today: fix(data.fits_today), maybe_skip: fix(data.maybe_skip) };
};

export const Planner = () => {
  const [settings, setSettings] = useState(null);
  const [entry, setEntry] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [habitLog, setHabitLog] = useState([]);
  const [focusOpen, setFocusOpen] = useState(false);
  const [winsOpen, setWinsOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const date = todayISO();

  const loadDay = useCallback(() => {
    getSettings().then(setSettings);
    getEntry(date)
      .then(setEntry)
      .catch(() => setEditing(true));
  }, [date]);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  const refreshTasks = useCallback(
    (force = false) => {
      if (!entry?.label) return;
      setTasksLoading(true);
      getTasks(entry.label, force, date)
        .then(setTasks)
        .finally(() => setTasksLoading(false));
    },
    [entry?.label, date]
  );

  useEffect(() => {
    if (entry?.label && !editing) {
      refreshTasks(false);
      getHabitLog(date).then((d) => setHabitLog(d.completed || []));
    }
  }, [entry?.label, editing, date, refreshTasks]);

  useEffect(() => {
    if (entry?.label && !editing && tasks && new Date().getHours() >= 18) {
      const key = `ebbly-wins-nudge-${date}`;
      if (!localStorage.getItem(key)) {
        setWinsOpen(true);
        localStorage.setItem(key, "1");
      }
    }
  }, [entry?.label, editing, tasks, date]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const result = await saveEntry({ date, ...payload });
      setEntry(result);
      setEditing(false);
      toast.success("Capacity calculated for today");
    } catch {
      toast.error("Could not save your check-in");
    }
    setSaving(false);
  };

  const onComplete = async (t, bucket) => {
    setTasks((d) => markCompleted(d, t.id, true));
    setCelebrate({ id: Date.now(), msg: DONE_MESSAGES[Math.floor(Math.random() * DONE_MESSAGES.length)] });
    try {
      await completeTask(t.id, date, t, bucket);
    } catch {
      toast.error("Couldn't complete that task");
      refreshTasks(true);
    }
  };

  const onReopen = async (t, bucket) => {
    setTasks((d) => markCompleted(d, t.id, false));
    try {
      await reopenTask(t.id, date);
    } catch {
      toast.error("Couldn't undo that task");
      refreshTasks(true);
    }
  };

  const onPostpone = async (t) => {
    try {
      await postponeTask(t.id);
      toast.success(`"${t.content.slice(0, 30)}" moved to tomorrow`);
      refreshTasks(true);
    } catch {
      toast.error("Couldn't postpone that task");
    }
  };

  const onToggleHabit = async (id) => {
    const res = await toggleHabit(date, id);
    setHabitLog(res.completed || []);
  };

  const onToggleSupplement = async (name) => {
    const taken = entry.supplements_taken || [];
    const next = taken.includes(name) ? taken.filter((x) => x !== name) : [...taken, name];
    setEntry((e) => ({ ...e, supplements_taken: next }));
    try {
      const updated = await saveEntry({
        date,
        cycle_day: entry.cycle_day,
        sleep_score: entry.sleep_score,
        sleep_source: entry.sleep_source,
        recovery: entry.recovery,
        mood: entry.mood,
        motivation: entry.motivation,
        supplements_taken: next,
      });
      setEntry(updated);
    } catch {
      toast.error("Couldn't save supplements");
    }
  };

  const progress = useMemo(
    () => computeProgress(tasks, habitLog, entry, settings?.supplements || []),
    [tasks, habitLog, entry, settings]
  );

  if (!settings) return <p className="text-stone-400">Loading…</p>;

  const showForm = editing || !entry;

  return (
    <div className="space-y-8">
      {celebrate && <Celebration key={celebrate.id} message={celebrate.msg} onDone={() => setCelebrate(null)} />}
      {showForm ? (
        <CheckInForm settings={settings} onSaved={handleSave} saving={saving} initial={entry} />
      ) : (
        <>
          <CapacityHero result={entry} progress={progress} />
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              data-testid="recheck-btn"
              onClick={() => setEditing(true)}
              className="rounded-full text-stone-500 hover:text-[#3b2a24] dark:text-stone-400 dark:hover:text-stone-100"
            >
              Update today's check-in
            </Button>
            <Button
              data-testid="see-wins-btn"
              onClick={() => setWinsOpen(true)}
              className="rounded-full gap-1.5 bg-[#b1543b] hover:bg-[#8f3f2b] text-white text-sm"
            >
              <Sparkles className="h-4 w-4" /> See today's wins
            </Button>
          </div>
          <TaskList
            data={tasks}
            loading={tasksLoading}
            label={entry.label}
            onComplete={onComplete}
            onReopen={onReopen}
            onPostpone={onPostpone}
            onRefresh={() => refreshTasks(true)}
            onOpenFocus={() => setFocusOpen(true)}
          />
          <TodaySchedule />
          <DailyHabits habits={tasks?.habits || []} completed={habitLog} onToggle={onToggleHabit} title={tasks?.essentials_name} />
          <SupplementsCard settings={settings} taken={entry.supplements_taken || []} onToggle={onToggleSupplement} />
          <FocusBlockDialog open={focusOpen} onOpenChange={setFocusOpen} tasks={tasks} onCreated={() => refreshTasks(true)} />
          <WinsRecap
            open={winsOpen}
            onOpenChange={setWinsOpen}
            entry={entry}
            tasks={tasks}
            habitLog={habitLog}
            settings={settings}
            progress={progress}
            date={date}
          />
          <p className="text-center text-xs text-stone-400 dark:text-stone-500 max-w-xs mx-auto pt-2">
            A personal wellness aid for planning — not medical advice, diagnosis, or birth control.
          </p>
        </>
      )}
    </div>
  );
};
